import { useContext, useEffect } from 'react';
import { LoadableComponentContext } from '@wix/da-hooks/pkg/useLoadableComponent';
import { CommentEditorSource } from '../redux/types';

/**
 * Forces our editor chunk to load if appropriate.
 *
 * Relies on the `LoadableComponentContext`.
 *
 * Some examples of when we'd want it to:
 * 1. A user clicked on a reply somewhere that doesn't have a comment editor mounted already.
 * 2. We're loading a draft.
 */
export function useForceLoadIfNeeded(
  editorSource: CommentEditorSource,
  shouldFocus: boolean,
  hasInitialContent: boolean
) {
  const { triggerLoad: triggerEditorLoad } = useContext(LoadableComponentContext);

  /**
   * Handle on-mount loading of the editor component when focus is requested, initial content is provided,
   * or this comment box is for a reply.
   *
   * Logic
   * - hasInitialContent - we have draft or something to edit right away
   * - source is a reply and its NOT forced to not focus - this handles clicking reply in a thread where you want to focus right away
   * - shouldFocus - there is a request out to focus this editor
   *
   */
  useEffect(() => {
    const forceLoadOnMount = hasInitialContent || shouldFocus;

    if (forceLoadOnMount) {
      triggerEditorLoad();
    }
  }, [triggerEditorLoad, editorSource, shouldFocus, hasInitialContent]);
}
